import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'PushNotificationButton',

  data: () => ({
    webSocketArray: [],
    buttonColor: 'white',
    autoReconnectClosedSockets: new Set(),
    autoReconnectIntervalId: null,
    tokenMillisLeft: 1,
    tokenExpirationTime: 0,
    currentDate: new Date()
  }),

  mounted: function () {
    this.startConnectLoop()
  },

  computed: {
    ...mapGetters({
      userId: 'userDataStore/userId',
      userIsAdmin: 'userDataStore/userIsAdmin',
      userPushMessagesEnable: 'userDataStore/pushMessagesEnable',
      'userProfile': 'userDataStore/userProfile',
      authorizedApplications: 'configurationStore/auth/applications',
      pushMessages: 'notificationStore/pushMessages',
      pushMessagesShow: 'notificationStore/pushMessagesShow',
      selectedContext: 'contextStore/selectedContext'
    })
  },

  methods: {
    ...mapActions({
      notifyError: 'notificationStore/notifyError',
      notifyMessage: 'notificationStore/notifyMessage',
      addPushMessage: 'notificationStore/addPushMessage',
      togglePushMessages: 'notificationStore/togglePushMessages'
    }),

    onClick: function () {
      this.togglePushMessages()
    },

    connectAllApplications: function () {
      // web sockets can be opened with expired token, e.g. after user session is ended
      if (this.getTokenMillisLeft() > 0) {
        // create connections to each remote ARPA service
        // autoReconnectClosedSockets indicates connection problems
        this.autoReconnectClosedSockets.clear()
        Object.values(this.authorizedApplications).forEach(application => {
          this.connectApplication(application)
        })

        if (this.autoReconnectClosedSockets.size === 0) {
          // all connections are active
          this.buttonColor = 'white'
        }
      } else {
        // token expired
        this.$debug('web socket keycloak token expired')
        this.buttonColor = 'blue-grey lighten-3'
      }
    },

    connectApplication: function (application) {
      if (application.notifierPush) {
        // make sure socket on same path does not exist
        const wsURL = this.computeWebSocketUrl(application.baseURL)
        const wsPath = this.stripWsUrlToken(wsURL)
        const webSocket = this.webSocketArray.find((ws) => ws.wsPath === wsPath)
        if (!webSocket) {
          // new web socket enriched with additional data
          const newWebSocket = new WebSocket(wsURL)
          newWebSocket.wsPath = wsPath
          newWebSocket.applicationId = application.id
          newWebSocket.tokenMillisLeft = this.tokenMillisLeft
          newWebSocket.tokenExpirationTime = this.tokenExpirationTime

          // save web socket
          this.webSocketArray.push(newWebSocket)

          // setup callbacks
          const thisVue = this

          newWebSocket.onopen = function () {
            const minutesLeft = Math.round(this.tokenMillisLeft / 60000)
            thisVue.$debug('web socket onopen', this.wsPath, minutesLeft, new Date().toLocaleString())
          }

          newWebSocket.onclose = function (event) {
            // indicate connection problem
            thisVue.autoReconnectClosedSockets.add(this.wsPath)
            thisVue.buttonColor = 'blue-grey lighten-3'
            thisVue.$debug('web socket onclose', this.wsPath, new Date().toLocaleString())
            // remove web socket
            const index = thisVue.webSocketArray.findIndex(ws => ws.wsPath === this.wsPath)
            if (index !== -1) {
              thisVue.webSocketArray.splice(index, 1)
            }
          }

          newWebSocket.onerror = function () {
            // indicate connection problem
            thisVue.autoReconnectClosedSockets.add(this.wsPath)
            thisVue.buttonColor = 'blue-grey lighten-3'
            thisVue.$debug('web socket onerror', this.wsPath)
          }

          newWebSocket.onmessage = function (message) {
            const arpaMsg = JSON.parse(message.data)
            arpaMsg.receptionTime = Date.now()
            if (arpaMsg.type === 'ERROR') {
              // show error to user
              const errorMsg = { message: arpaMsg.subject }
              thisVue.notifyError(errorMsg)
            } else if (arpaMsg.type === 'KEEPALIVE') {
              // keep alive msg
              thisVue.$debug('web socket keepalive', this.wsPath)
            } else {
              // new message
              thisVue.addPushMessage(arpaMsg)
            }
          }
        } else {
          // socket already exists
          if (webSocket.tokenExpirationTime > 0) {
            // working with keycloak token
            const tokenTimeLeft = webSocket.tokenExpirationTime - this.currentDate.getTime()
            if (tokenTimeLeft < 0) {
              // close socket when token has expired
              this.$debug('web socket token has expired', webSocket.wsPath, this.currentDate.toLocaleString())
              webSocket.close()
            }
          }
        }
      }
    },

    getTokenMillisLeft: function () {
      // this method is called each time connections are evaluated
      // set keycloak token default values
      this.currentDate = new Date()
      this.tokenExpirationTime = 0
      this.tokenMillisLeft = 1
      if (Vue.prototype.$keycloak) {
        // save token information to be injected into socket objects for later use
        const keycloak = Vue.prototype.$keycloak
        this.tokenExpirationTime = keycloak.tokenParsed.exp * 1000
        this.tokenMillisLeft = this.tokenExpirationTime - this.currentDate.getTime()
        if (this.tokenMillisLeft <= 0) {
          // keycloak token and user session have expired
          this.notifyError({ message: this.$t('error.userSessionExpired') })
        }
      }
      return this.tokenMillisLeft
    },

    computeWebSocketUrl: function (baseURL) {
      // end point example wss://localhost:8091/apiws/notification/lsuardi?token=...
      const url = this.$axios.agilisUrlTrackerPushNotification(this.userId)
      const token = Vue.prototype.$keycloak != null ? Vue.prototype.$keycloak.token : undefined
      const tokenParam = token ? '?token=' + token : ''
      // when working with keycloak in local dev set protocol to ws://
      const protocol = token ? 'wss://' : 'ws://'
      const wsURL = baseURL.replace('https://', protocol).replace('http://', protocol) + url + tokenParam

      return wsURL
    },

    startConnectLoop: function () {
      // infinite connection monitoring loop
      this.stopConnectLoop()
      if (this.userPushMessagesEnable) {
        if ('WebSocket' in window) {
          this.$debug('web socket start connect loop', this.currentDate.toLocaleString())
          this.connectAllApplications()
          const thisVue = this
          // monitor connections every 32 seconds
          this.autoReconnectIntervalId = setInterval(function () {
            const minutesLeft = Math.round(thisVue.tokenMillisLeft / 60000)
            thisVue.$debug('web socket connect loop',
              thisVue.autoReconnectIntervalId, new Date().toLocaleString(),
              minutesLeft, thisVue.connectedApplications())
            thisVue.connectAllApplications()
          }, 32000)
        } else {
          this.$debug('web socket not supported')
        }
      }
    },

    stopConnectLoop: function () {
      if (this.autoReconnectIntervalId) {
        clearInterval(this.autoReconnectIntervalId)
        this.buttonColor = 'blue-grey lighten-3'
        this.$debug('web socket stop connect loop', this.autoReconnectIntervalId, this.currentDate.toLocaleString())
        this.autoReconnectIntervalId = null
      }
      this.webSocketArray.forEach(webSocket => webSocket.close())
      this.webSocketArray = []
    },

    stripWsUrlToken: function (wsUrl) {
      // const tokenIndex = wsUrl.indexOf('?token=')
      // return tokenIndex > 0 ? wsUrl.substring(0, tokenIndex) : wsUrl
      return wsUrl.split('?')[0]
    },

    connectedApplications: function () {
      return Object.keys(this.authorizedApplications).filter(key => {
        const application = this.authorizedApplications[key]
        const appServiceHost = application.baseURL.split('/')[2]
        const webSocket = this.webSocketArray.find((ws) => ws.wsPath.indexOf(appServiceHost) >= 0)
        return webSocket != null && application.notifierPush
      }).join(',')
    }
  },

  watch: {
    'userProfile' () {
      if (this.userProfile.settings.pushMessagesEnable) {
        // connect when push messages enabled
        this.startConnectLoop()
      } else {
        this.stopConnectLoop()
        if (this.pushMessagesShow) {
          // close push messages panel when messages disabled
          this.togglePushMessages()
        }
      }
    }
  }

}

/*
// DEPRECATED
startAutoReconnect: function (webSocket) {
  // startAutoReconnect is called when a connection is interrupted
  // autoReconnectClosedSockets set keeps track of failed connections
  this.autoReconnectClosedSockets.add(webSocket.wsPath)
  if (!this.autoReconnectIntervalId) {
    this.buttonColor = 'blue-grey lighten-3'
    const thisVue = this
    // attempt reconnecting every 25 seconds
    this.autoReconnectIntervalId = setInterval(function () {
      thisVue.$debug('auto reconnect ' + thisVue.autoReconnectIntervalId)
      thisVue.connectAllApplications()
    }, 25000)
  }
},

// DEPRECATED
stopAutoReconnect: function (webSocket) {
  // stopAutoReconnect is called when a connection is resumed
  // autoReconnectClosedSockets set keeps track of failed connections
  this.autoReconnectClosedSockets.delete(webSocket.wsPath)
  if (this.autoReconnectClosedSockets.size === 0) {
    // all connections are active
    this.buttonColor = 'white'
    if (this.autoReconnectIntervalId) {
      this.$debug('auto reconnect stop ' + this.autoReconnectIntervalId)
      clearInterval(this.autoReconnectIntervalId)
      this.autoReconnectIntervalId = null
    }
  }
},
*/
